import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'

interface Props {
  children: ReactNode
  // Fallback alternativo para secciones específicas (más chico que la pantalla completa)
  inline?: boolean
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[MayoExpress] Error no controlado:', error, info.componentStack)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleReset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.inline) return <InlineError onReset={this.handleReset} />
    return <FullScreenError error={error} onReload={this.handleReload} />
  }
}

// ─── Pantalla completa (para errores globales) ────────────────────────────────

function FullScreenError({ error, onReload }: { error: Error; onReload: () => void }) {
  const isDev = import.meta.env.DEV

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--me-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--me-space-8)',
    }}>
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--me-space-6)' }}>

        {/* Logo mark */}
        <div style={{
          width: 52, height: 52,
          background: 'var(--me-bad-soft)',
          borderRadius: 14,
          clipPath: 'polygon(0 0, 100% 0, 100% 75%, 75% 100%, 0 100%)',
        }} />

        <div className="me-card" style={{ width: '100%', padding: 'var(--me-space-8)', textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'var(--me-bad-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto var(--me-space-5)',
          }}>
            <svg viewBox="0 0 24 24" width={22} height={22} fill="none"
              stroke="var(--me-bad)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>

          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>MayoExpress</div>
          <h1 className="me-page-title" style={{ marginBottom: 'var(--me-space-3)', fontSize: 22 }}>
            Algo salió mal
          </h1>
          <p style={{ fontSize: 14, color: 'var(--me-text-3)', lineHeight: 1.6, marginBottom: 'var(--me-space-6)' }}>
            Ocurrió un error inesperado. Tus datos están seguros —
            recargá la página para continuar.
          </p>

          {isDev && (
            <div style={{
              textAlign: 'left',
              padding: '12px 14px',
              borderRadius: 8,
              background: 'var(--me-surface-2)',
              border: '1px solid var(--me-line)',
              marginBottom: 'var(--me-space-5)',
              fontFamily: 'var(--me-mono)',
              fontSize: 11.5,
              color: 'var(--me-bad)',
              lineHeight: 1.5,
              wordBreak: 'break-word',
            }}>
              {error.message}
            </div>
          )}

          <button
            className="me-btn me-btn-primary"
            style={{ width: '100%' }}
            onClick={onReload}
          >
            Recargar página
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Inline (para secciones dentro de la app) ─────────────────────────────────

function InlineError({ onReset }: { onReset: () => void }) {
  return (
    <div style={{
      padding: 'var(--me-space-8)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 'var(--me-space-4)',
      textAlign: 'center',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: 'var(--me-bad-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none"
          stroke="var(--me-bad)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--me-text)', marginBottom: 4 }}>
          Error al cargar esta sección
        </div>
        <div style={{ fontSize: 13, color: 'var(--me-text-3)' }}>
          El resto de la app sigue funcionando
        </div>
      </div>
      <button className="me-btn me-btn-ghost" onClick={onReset}>
        Reintentar
      </button>
    </div>
  )
}
